/**
 * Shared Signal Benchmarking System
 *
 * This module provides consistent color coding for performance metrics
 * across all Birdbrain analytics views (Funnel, Campaign Manager, etc.)
 *
 * Signal Levels:
 * - Excellent: Outstanding performance (strong green)
 * - Good: Above average (light green)
 * - Neutral/Average: Within normal range (blue)
 * - Warning/Weak: Needs attention (yellow)
 * - Poor: Action required (red)
 */

// ============================================
// Types
// ============================================

export type SignalLevel = 'excellent' | 'good' | 'neutral' | 'warning' | 'poor' | 'maturing'

/** Threshold-derived levels only — a metric getter never yields 'maturing'. */
export type MetricSignalLevel = Exclude<SignalLevel, 'maturing'>

export interface SignalStyle {
  text: string
  bg: string
  signal: SignalLevel
}

export type AdPlatform = 'facebook' | 'google' | 'apple'

interface PlatformCtrBenchmark {
  excellent: number
  good: number
  average: number
  warning: number
}

// ============================================
// Signal Styles
// ============================================

/**
 * 5-level color system for performance signals.
 * Uses semantic colors independent of brand palette:
 * - Emerald for positive signals (excellent/good)
 * - Blue for neutral/average
 * - Yellow/Amber for warnings
 * - Red for poor/critical
 */
export const SIGNAL_STYLES: Record<SignalLevel, SignalStyle> = {
  excellent: {
    text: 'text-emerald-700 dark:text-emerald-400 font-semibold',
    bg: 'bg-emerald-100 dark:bg-emerald-800/50',
    signal: 'excellent',
  },
  good: {
    text: 'text-emerald-600 dark:text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-900/50',
    signal: 'good',
  },
  neutral: {
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-950/50',
    signal: 'neutral',
  },
  warning: {
    text: 'text-yellow-700 dark:text-yellow-400',
    bg: 'bg-yellow-50 dark:bg-yellow-950/50',
    signal: 'warning',
  },
  poor: {
    text: 'text-red-700 dark:text-red-400 font-semibold',
    bg: 'bg-red-100 dark:bg-red-950/50',
    signal: 'poor',
  },
  // Cohort window not closed yet: render a muted dash, never a fake 0/red.
  maturing: {
    text: 'text-muted-foreground',
    bg: 'bg-transparent',
    signal: 'maturing',
  },
}

/** Placeholder for metrics whose measurement window hasn't closed. */
export const MATURING_PLACEHOLDER = '—'

// ============================================
// Platform-Specific CTR Benchmarks
// ============================================

/**
 * Industry-standard CTR benchmarks by ad platform (2024 data).
 *
 * Each platform has different user intent and ad formats,
 * resulting in vastly different CTR expectations.
 */
export const PLATFORM_CTR_BENCHMARKS: Record<AdPlatform, PlatformCtrBenchmark> = {
  facebook: {
    // Facebook/Meta: Display-like, lower CTR expected
    // Industry avg: 1.0% - 1.8% (2024)
    excellent: 2.5,
    good: 1.8,
    average: 1.0,
    warning: 0.5,
  },
  google: {
    // Google Search Ads for Productivity/B2B
    // Cross-industry avg: 6.66%, B2B: 5.17%, Tech: 2.09%
    // Productivity tools realistic avg: 3-5%
    excellent: 7.0,
    good: 5.0,
    average: 3.0,
    warning: 1.5,
  },
  apple: {
    // Apple Search Ads: High-intent App Store users
    // Industry avg: ~4% (2024)
    excellent: 6.0,
    good: 4.0,
    average: 2.5,
    warning: 1.5,
  },
}

/**
 * Blended CTR benchmarks for multi-channel or unknown platforms.
 * Used as fallback when platform is not specified.
 */
const BLENDED_CTR_BENCHMARK: PlatformCtrBenchmark = {
  excellent: 5.0,
  good: 3.5,
  average: 2.0,
  warning: 1.0,
}

// ============================================
// CTR Signal Functions
// ============================================

/**
 * Get CTR signal for a specific platform with platform-appropriate benchmarks.
 *
 * @param platform - The ad platform (facebook, google, apple) or null for blended
 * @param ctr - Click-through rate as percentage (e.g., 2.5 for 2.5%)
 */
export function getCtrSignal(ctr: number, platform?: AdPlatform | string | null): SignalStyle {
  // Normalize platform name to lowercase and extract base platform
  const normalizedPlatform = normalizePlatformName(platform)
  const benchmarks = normalizedPlatform
    ? PLATFORM_CTR_BENCHMARKS[normalizedPlatform]
    : BLENDED_CTR_BENCHMARK

  if (ctr >= benchmarks.excellent) return SIGNAL_STYLES.excellent
  if (ctr >= benchmarks.good) return SIGNAL_STYLES.good
  if (ctr >= benchmarks.average) return SIGNAL_STYLES.neutral
  if (ctr >= benchmarks.warning) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Get tooltip text explaining CTR benchmarks for a platform.
 */
export function getCtrTooltip(platform?: AdPlatform | string | null): string {
  const normalizedPlatform = normalizePlatformName(platform)
  const benchmarks = normalizedPlatform
    ? PLATFORM_CTR_BENCHMARKS[normalizedPlatform]
    : BLENDED_CTR_BENCHMARK

  const platformName = normalizedPlatform
    ? `${normalizedPlatform.charAt(0).toUpperCase() + normalizedPlatform.slice(1)} Ads`
    : 'Blended'

  return `CTR for ${platformName}\n\nLegend:\n• Excellent: ≥${benchmarks.excellent}%\n• Good: ${benchmarks.good}-${benchmarks.excellent}%\n• Average: ${benchmarks.average}-${benchmarks.good}%\n• Weak: ${benchmarks.warning}-${benchmarks.average}%\n• Poor: <${benchmarks.warning}%`
}

/**
 * Normalize various platform name formats to our standard AdPlatform type.
 */
function normalizePlatformName(platform?: string | null): AdPlatform | null {
  if (!platform) return null

  const lower = platform.toLowerCase()

  if (lower.includes('facebook') || lower.includes('meta') || lower === 'fb') {
    return 'facebook'
  }
  if (lower.includes('google') || lower === 'goog') {
    return 'google'
  }
  if (lower.includes('apple') || lower === 'asa') {
    return 'apple'
  }

  return null
}

// ============================================
// Landing Page Signal Functions
// ============================================

/**
 * LP CTA Rate signal - CTA Clicks ÷ LP Views
 *
 * Measures how effectively the landing page converts visitors to CTA clicks.
 * Based on 2024 research: typical CTA rates 2-5%, top performers 15-20%.
 *
 * @param rate - Conversion rate as percentage (e.g., 7.5 for 7.5%)
 */
export function getLpCtaRateSignal(rate: number): SignalStyle {
  if (rate >= 10) return SIGNAL_STYLES.excellent
  if (rate >= 7) return SIGNAL_STYLES.good
  if (rate >= 4) return SIGNAL_STYLES.neutral
  if (rate >= 2) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

// ============================================
// Funnel Stage Signal Functions
// ============================================

/**
 * Signup Rate signal - Signups ÷ App Opens
 *
 * Measures registration conversion - how many first-time app openers complete signup.
 *
 * @param rate - Conversion rate as percentage
 */
export function getSignupRateSignal(rate: number): SignalStyle {
  if (rate >= 70) return SIGNAL_STYLES.excellent
  if (rate >= 50) return SIGNAL_STYLES.good
  if (rate >= 30) return SIGNAL_STYLES.neutral
  if (rate >= 15) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Onboarding Rate signal - Onboarded ÷ Signups
 *
 * Measures onboarding completion - how many signed-up users complete the setup flow.
 *
 * @param rate - Conversion rate as percentage
 */
export function getOnboardingRateSignal(rate: number): SignalStyle {
  if (rate >= 80) return SIGNAL_STYLES.excellent
  if (rate >= 60) return SIGNAL_STYLES.good
  if (rate >= 40) return SIGNAL_STYLES.neutral
  if (rate >= 20) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Subscribe Rate signal - Subscribed ÷ Signups
 *
 * Subscribe = user got past the paywall (trial OR direct purchase). With our
 * hard paywall after onboarding, this is the make-or-break funnel step.
 *
 * Thresholds calibrated to observed range (9-17% across recent cohorts).
 *
 * @param rate - Conversion rate as percentage
 */
export function getSubscribeRateSignal(rate: number): SignalStyle {
  if (rate >= 20) return SIGNAL_STYLES.excellent
  if (rate >= 14) return SIGNAL_STYLES.good
  if (rate >= 10) return SIGNAL_STYLES.neutral
  if (rate >= 6) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Activation Rate signal - Activated ÷ Signups
 *
 * Activation = user sent first Composer Message within 7 days of first app open.
 * This is a strict, predictive bar: activated users pay at ~46x baseline.
 *
 * Thresholds are calibrated to observed activation/signup ratios, not to
 * industry benchmarks (which assume much looser activation definitions).
 *
 * @param rate - Conversion rate as percentage
 */
export function getActivationRateSignal(rate: number): SignalStyle {
  if (rate >= 10) return SIGNAL_STYLES.excellent
  if (rate >= 7) return SIGNAL_STYLES.good
  if (rate >= 5) return SIGNAL_STYLES.neutral
  if (rate >= 3) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Trial Rate signal - Trials ÷ Signups
 *
 * Measures how effectively signups convert to starting a free trial.
 *
 * Benchmarks (2025-2026):
 * - Healthy opt-in funnels: 15-20% signup-to-trial (Growth Unhinged 2026)
 * - Freemium products: 10-15% typical
 * - Bottom quartile: <5%
 *
 * @param rate - Conversion rate as percentage
 */
export function getTrialRateSignal(rate: number): SignalStyle {
  if (rate >= 18) return SIGNAL_STYLES.excellent
  if (rate >= 10) return SIGNAL_STYLES.good
  if (rate >= 5) return SIGNAL_STYLES.neutral
  if (rate >= 2) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Paid Rate signal - Paid ÷ Signups
 *
 * Measures full signup-to-paid conversion efficiency.
 *
 * Benchmarks (2025-2026):
 * - Freemium median: ~5.5% (Growth Unhinged 2026, 200 products)
 * - Freemium GOOD: 3-5%, GREAT: 8-12%
 * - AI products GOOD: 6-8%, GREAT: 15-20%
 * - Products <$500 ACV: ~9% median signup-to-paid
 *
 * @param rate - Conversion rate as percentage
 */
export function getPaidRateSignal(rate: number): SignalStyle {
  if (rate >= 10) return SIGNAL_STYLES.excellent
  if (rate >= 5) return SIGNAL_STYLES.good
  if (rate >= 2) return SIGNAL_STYLES.neutral
  if (rate >= 1) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

// ============================================
// Cost Signal Functions
// ============================================

/**
 * CAC (Customer Acquisition Cost) signal - inverse scale (lower is better)
 *
 * @param cac - Cost in dollars to acquire one paying customer
 */
export function getCacSignal(cac: number): SignalStyle {
  if (cac <= 50) return SIGNAL_STYLES.excellent
  if (cac <= 100) return SIGNAL_STYLES.good
  if (cac <= 200) return SIGNAL_STYLES.neutral
  if (cac <= 400) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * ROAS (Return on Ad Spend) signal - cumulative cohort revenue ÷ ad spend.
 *
 * This is lifetime, cohort-attributed ROAS (not a windowed snapshot), so 1.0x =
 * breakeven on the cohorts acquired by the campaign. For a subscription product
 * whose revenue accrues over months, ≥1.0x is the goal and ≥1.5x is strong.
 *
 * @param roas - Revenue divided by spend, as a ratio (e.g. 1.25 for 125%)
 */
export function getRoasSignal(roas: number): SignalStyle {
  if (roas >= 1.5) return SIGNAL_STYLES.excellent
  if (roas >= 1.0) return SIGNAL_STYLES.good
  if (roas >= 0.7) return SIGNAL_STYLES.neutral
  if (roas >= 0.4) return SIGNAL_STYLES.warning
  return SIGNAL_STYLES.poor
}

/**
 * Cohorts younger than this (in months) haven't had time to recoup spend, so
 * their ROAS is shown muted/neutral rather than graded against the thresholds.
 */
export const ROAS_MATURITY_MONTHS = 3

// ============================================
// Helper to get signal for any funnel stage
// ============================================

export type FunnelStage =
  | 'app_opened'
  | 'signed_up'
  | 'onboarded'
  | 'subscribed'
  | 'activated'
  | 'trial_started'
  | 'converted'

/**
 * Get the appropriate signal function for a funnel stage.
 * Returns the signal style for the given rate.
 *
 * @param stage - The funnel stage
 * @param rate - Conversion rate as percentage
 */
export function getFunnelStageSignal(stage: FunnelStage, rate: number): SignalStyle {
  switch (stage) {
    case 'app_opened':
      // App opened doesn't have a rate signal (it's the base)
      return SIGNAL_STYLES.neutral
    case 'signed_up':
      return getSignupRateSignal(rate)
    case 'onboarded':
      return getOnboardingRateSignal(rate)
    case 'subscribed':
      return getSubscribeRateSignal(rate)
    case 'activated':
      return getActivationRateSignal(rate)
    case 'trial_started':
      return getTrialRateSignal(rate)
    case 'converted':
      return getPaidRateSignal(rate)
    default:
      return SIGNAL_STYLES.neutral
  }
}

/**
 * Get the SignalLevel for a funnel stage (useful for drill-down data).
 *
 * @param stage - The funnel stage
 * @param rate - Conversion rate as decimal (0-1)
 */
export function getSignalLevelForStage(stage: FunnelStage, rate: number): MetricSignalLevel {
  const ratePercent = rate * 100
  // Threshold getters only ever select the five metric styles.
  return getFunnelStageSignal(stage, ratePercent).signal as MetricSignalLevel
}
